import isDarkColor from 'is-dark-color';

import {
  AppAlertColors,
  AppColorStrings,
  AppSpacingStrings,
  Theme,
} from '../styles';

export const getColorFromTheme: ThemeGetterFn<AppColorStrings> = (
  props,
  color
) => props.theme.colors[color];

export const getFontSizeFromTheme: ThemePropGetterFn<number> = props =>
  props.theme.fontSize;

export const getPaddingFromTheme: ThemeGetterFn<AppSpacingStrings> = (
  props,
  size
) => props.theme.spacing.padding[size];

export const getAlertThemeColor: ThemeGetterFn<AppAlertColors> = (
  props,
  alertLevel
) => props.theme.colors[alertColors[alertLevel]];

export const getContrastColor: ThemeGetterFn<AppColorStrings> = (
  props: HasThemeProp,
  color: AppColorStrings
) =>
  getColorFromTheme(
    props,
    isDarkColor(getColorFromTheme(props, color)) ? 'white' : 'black'
  );

export const getAlertText = (status: AppAlertColors) => alertTitles[status];
export const getAlertMessage = (status: AppAlertColors) =>
  alertMessages[status];

const alertMessages: { [key in AppAlertColors]: string } = {
  DANGER:
    "It looks like membership in Alfred service won't meet your monthly goal. Let's find time to talk so we can work together to get more of your residents using the service.",
  GOOD:
    "Membership in Alfred Service is on track to meet your monthly goal. We'd love to know what you think is working particularly well and how we can help your building community get the most out of life with Hello Alfred.",
  RISK:
    'You are a little behind your goal for this month. Check the resources section to learn more about how you can improve membership.',
};
const alertTitles: { [key in AppAlertColors]: string } = {
  DANGER: 'Needs Improvement',
  GOOD: 'Good',
  RISK: 'Satisfactory',
};

export const alertColors: { [key in AppAlertColors]: AppColorStrings } = {
  DANGER: 'alertDanger',
  GOOD: 'alertGood',
  RISK: 'alertRisk',
};

export const getCSSPosition = (x: PositionX, y: PositionY) => ({
  ...calculateCSSPosition(x),
  ...calculateCSSPosition(y),
  ...calculateCSSTransformation(x, y),
});

export type PositionX = 'left' | 'center' | 'right';
export type PositionY = 'top' | 'middle' | 'bottom';
export type Position = PositionX | PositionY;
type ThemeGetterFn<T> = (props: HasThemeProp, element: T) => string;
type ThemePropGetterFn<T> = (prop: HasThemeProp) => T;

const calculateCSSTransformation = (x: PositionX, y: PositionY) => {
  if (x === 'center' && y === 'middle') {
    return { transform: 'translate(-50%, -50%)' };
  }
  if (x === 'center') {
    return { transform: 'translateX(-50%)' };
  }
  if (y === 'middle') {
    return { transform: 'translateY(-50%)' };
  }

  return {};
};

const calculateCSSPosition = (pos: Position) => {
  switch (pos) {
    case 'center':
      return { left: '50%', right: 'initial' };
    case 'middle':
      return { bottom: 'initial', top: '50%' };
    case 'left':
    case 'right':
    case 'top':
    case 'bottom':
      return { [pos]: '1em' };
    default:
      return {};
  }
};

interface HasThemeProp {
  theme: Theme;
}

// Taken from: https://github.com/villadora/hexrgb/blob/master/index.js
export const hex2rgb = (hexValue: string, array: boolean) => {
  let hex = String(hexValue).match(/^#?(\w{1,2})(\w{1,2})(\w{1,2})$/);
  if (hex) {
    hex = hex.slice(1);
    if (hex.length !== 3) {
      return [];
    }
    const rgb = [];
    for (let i = 0; i < 3; i += 1) {
      let value = hex[i];
      if (value.length === 1) {
        value += value;
      }
      rgb.push(parseInt(value, 16));
    }
    return array ? rgb : `rgb(${rgb})`;
  }

  return [];
};
